import { Component, ElementRef, Inject, OnDestroy, OnInit, Renderer2, ViewChild, ViewContainerRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ComponentContainer } from 'golden-layout';
import { Store } from '@ngrx/store';
import { EditEvent, GridComponent, RemoveEvent } from '@progress/kendo-angular-grid';
import { distinct } from '@progress/kendo-data-query';
import { BehaviorSubject } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

import { IZoomEvent } from '../../../_models/models';
import { SpDialogEditComponent } from '../../_dialogs/sp-dialog-edit/sp-dialog-edit.component';
import { GoldenLayoutContainerInjectionToken, WidgetHostDirective } from '../../../_directives/widget-host.directive';
import { DashboardService } from '../../dashboard/dashboard.service';
import { SpService } from '../../../_services/http/sp.service';
import { IActivationSp, IScenarioPreprogramme, ScenarioPreprogramme } from '../../../_models/ScenarioPreprogramme';
import { EtapeService } from '../../../_services/http/etape.service';
import { MainStoreState } from '../../../_store/mainStoreState';
import {
  deepLoadScenarioPreprogrammeAction,
  loadScenarioPreprogrammesAction,
  selectAllScenarioPreprogrammes,
  selectedScenarioPreprogramme,
} from '../../../_store/ScenarioPreprogrammeStore/scenario-preprogramme.store';
import { SpDialogEtapeListComponent } from '../../_dialogs/sp-dialog-etape-list/sp-dialog-etape-list.component';
import { ConfigService } from '../../../_services/local/config.service';
import { EsriHelpers } from '../../../_helpers/EsriHelpers';
import { loadPlanGestionMobilitesAction } from '../../../_store/PlanGestionMobiliteStore/plan-gestion-mobilite.store';
import { PgmService } from '../../../_services/http/pgm.service';
import { GridWrapper } from '../../../_models/GridWrapper';
import { ConfirmService } from '../../../_services/local/confirm.service';
import { SessionEdition } from 'src/app/_models/SessionEdition';
import { getCurrentUser } from 'src/app/_store/AccountStore/account.store';
import { hasSessionEdition } from 'src/app/_store/SessionStore/session.store';

@Component({
  selector: 'app-widget-sp-list',
  templateUrl: './sp-list.component.html',
  styleUrls: ['./sp-list.component.scss'],
})
export class SpListComponent extends WidgetHostDirective implements OnInit, OnDestroy {
  @ViewChild('kendoGrid', { static: true }) kendoGrid!: GridComponent;

  private channel = new BroadcastChannel('map-vec');

  public existsSessionForCurrentUser$ = new BehaviorSubject<SessionEdition | undefined>(undefined);

  // Grid attributes
  public gridWrapper = new GridWrapper<ScenarioPreprogramme>(
    'ScenarioPreprogramme',
    this.store.select(selectAllScenarioPreprogrammes),
    {
      logic: 'or',
      filters: [
        {
          field: 'nom',
          operator: 'contains',
        },
        {
          field: 'descriptionTypeSp',
          operator: 'contains',
        },
        {
          field: 'etapesActives',
          operator: 'contains',
        },
      ],
    },
    this.kendoGrid
  );

  private shouldOpenEdit = false;
  private shouldOpenEditAsReadOnly = true;
  private shouldOpenActivationEtape = false;
  private shouldOpenScenarioEtape = false;
  private selectedSp: ScenarioPreprogramme | undefined;
  private sub = this.store.select(selectedScenarioPreprogramme).subscribe((sp) => {
    this.selectedSp = sp;
    if (this.shouldOpenEdit && this.selectedSp) {
      this.openEditDialog(this.selectedSp);
      this.shouldOpenEdit = false;
    }

    if (this.shouldOpenActivationEtape && sp) {
      this.openActivationEtapesDialog(sp);
      this.shouldOpenActivationEtape = false;
    }

    if (this.shouldOpenScenarioEtape && sp) {
      this.openActivationScenario(sp);
      this.shouldOpenScenarioEtape = false;
    }
  });

  constructor(
    @Inject(GoldenLayoutContainerInjectionToken) container: ComponentContainer,
    elRef: ElementRef,
    renderer: Renderer2,
    viewContainerRef: ViewContainerRef,
    store: Store<MainStoreState>,
    private dashboardService: DashboardService,
    private dialog: MatDialog,
    private etapeService: EtapeService,
    private pgmService: PgmService,
    public crudService: SpService,
    private configService: ConfigService,
    private confirmService: ConfirmService
  ) {
    super(container, elRef.nativeElement, renderer, viewContainerRef, store);

    EsriHelpers.setSymbols(this.configService.Config.layers.sp.symbol);
  }

  ngOnInit(): void {
    this.store.dispatch(loadScenarioPreprogrammesAction());
    this.store.dispatch(loadPlanGestionMobilitesAction());
    this.crudService.refreshHubConnection();
    this.etapeService.refreshHubConnection();
    this.pgmService.refreshHubConnection();

    this.store
      .select(getCurrentUser)
      .pipe(
        mergeMap((user) => {
          return this.store.select(hasSessionEdition(user));
        })
      )
      .subscribe((sessionEdition) => {
        if (sessionEdition) {
          this.existsSessionForCurrentUser$.next(sessionEdition);
        } else {
          this.existsSessionForCurrentUser$.next(undefined);
        }
      });
  }

  ngOnDestroy(): void {
    this.gridWrapper.destroy();
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }

  public zoomTo(event: MouseEvent, sp: IScenarioPreprogramme): void {
    event.stopPropagation();

    if (!this.configService.Config.layers) {
      return;
    }

    const data: IZoomEvent<IScenarioPreprogramme> = {
      type: 'zoom',
      title: this.configService.Config.layers.sp.title,
      searchField: this.configService.Config.layers.sp.searchField,
      searchText: sp.id.toString(),
      layerUrl: this.configService.Config.layers.sp.url,
      subLayerIds: this.configService.Config.layers.sp.layerIds,
    };

    if (this.dashboardService.isWidgetOpen('MapVecComponent')) {
      this.channel.postMessage(data);
      this.dashboardService.focusWidget('MapVecComponent');
    } else {
      this.dashboardService.openWidget('MapVecComponent', data);
    }
  }

  public toggleActivationEtapesDialog(sp: IScenarioPreprogramme): void {
    this.shouldOpenActivationEtape = true;
    this.store.dispatch(deepLoadScenarioPreprogrammeAction({ id: sp.id }));
  }

  public toggleActivationScenario(sp: IScenarioPreprogramme) {
    this.shouldOpenScenarioEtape = true;
    this.store.dispatch(deepLoadScenarioPreprogrammeAction({ id: sp.id }));
  }

  public delete(event: RemoveEvent): void {
    this.confirmService
      .open(`Voulez-vous supprimer le scenario préprogrammé`, event.dataItem.nom, this.crudService.delete(event.dataItem.id))
      .then((res) => {
        if (res) {
          console.debug('supprimé', event);
        }
      });
  }

  addNewSp() {
    this.dialog.open(SpDialogEditComponent, {
      width: '90%',
      height: '80%',
      data: { sp: undefined, isReadOnly: false },
    });
  }

  dispatchDeepLoadSelectedItem(item: EditEvent | ScenarioPreprogramme, isReadOnly?: boolean): void {
    this.shouldOpenEdit = true;
    this.shouldOpenEditAsReadOnly = isReadOnly === true;
    const sp = 'dataItem' in item ? item.dataItem : item;
    this.store.dispatch(deepLoadScenarioPreprogrammeAction({ id: sp.id }));
  }

  openEditDialog(sp: ScenarioPreprogramme | undefined): void {
    this.dialog.open(SpDialogEditComponent, {
      width: '90%',
      height: '80%',
      data: { sp, isReadOnly: this.shouldOpenEditAsReadOnly },
    });
  }

  openActivationEtapesDialog(sp: ScenarioPreprogramme): void {
    this.dialog.open(SpDialogEtapeListComponent, {
      width: '90%',
      height: '90%',
      data: { sp },
    });
  }

  public openActivationScenario(sp: ScenarioPreprogramme) {
    const actif = !sp.actif;

    const activation: IActivationSp = {
      id: sp.id,
      actif,
      etapes: sp.etapes.map((x) => ({ id: x.id, actif: actif ? x.actif : false })),
    };

    const text = actif ? 'activer' : 'désactiver';
    this.confirmService.open(`Voulez-vous ${text} le scenario préprogrammé`, sp.nom, this.crudService.activate(activation)).then((res) => {
      if (res) {
        console.debug(actif, sp);
      }
    });
  }

  public distinctPrimitive(fieldName: string): unknown[] {
    return distinct(this.gridWrapper.gridData.data, fieldName).map(
      // @ts-ignore
      (item) => item[fieldName]
    );
  }
}
